import { useState, useEffect } from "react";
import { apiGet, ApiResponse } from "../../services/api.service";

interface EventWithStatus {
	event_id: number;
	event_date: string;
	event_type: string;
	start_time: string;
	end_time: string;
	extra_required: number;
	extra_booked: number;
	extra_applied: number;
	application_status?: "applied" | "booked" | "cancelled" | "withdrawn";
	applied: boolean;
	can_withdraw?: boolean;
}

interface UseEventsWithStatusReturn {
	events: EventWithStatus[];
	loading: boolean;
	error: string | null;
	refetch: () => Promise<void>;
}

/**
 * Custom hook to fetch available events with user's application status
 * @param extraId - The ID of the extra (user)
 * @param eventType - Filter by event type: 'internal', 'external', or null for all
 * @returns Object containing events with status, loading state, error state, and refetch function
 */
export function useEventsWithStatus(
	extraId: number | null,
	eventType: "internal" | "external" | null
): UseEventsWithStatusReturn {
	const [events, setEvents] = useState<EventWithStatus[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	const fetchEvents = async () => {
		if (!extraId) {
			setEvents([]);
			return;
		}

		try {
			setLoading(true);
			setError(null);

			// Build URL with optional type parameter
			let url = `/events/available/${extraId}`;
			const params: any = {};
			if (eventType) {
				params.type = eventType;
			}

			const response: ApiResponse<EventWithStatus[]> = await apiGet<
				EventWithStatus[]
			>(url, params);

			if (response.success && response.data) {
				setEvents(response.data);
			} else {
				setError(response.error || "Failed to fetch events");
				setEvents([]);
			}
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : "Failed to fetch events";
			setError(errorMessage);
			setEvents([]);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchEvents();
	}, [extraId, eventType]);

	return {
		events,
		loading,
		error,
		refetch: fetchEvents,
	};
}
